import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { buildReportHTML } from './analysisReportTemplate';

const PAGE_MARGIN = 32; // pt
const IMG_QUALITY = 0.72; // JPEG — keeps file size small, still crisp for text
const CANVAS_SCALE = 2; // retina without the 3x/4x size jump
const REPORT_WIDTH_PX = 794; // A4 width @96dpi

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const createOffscreenContainer = (html) => {
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '-99999px'; // offscreen but still laid out/rendered
  container.style.width = `${REPORT_WIDTH_PX}px`;
  container.style.background = '#ffffff';
  container.innerHTML = html;
  document.body.appendChild(container);
  return container;
};

const getBlockBoundaries = (container) =>
  Array.from(container.querySelectorAll('[data-report-block]')).map((el) => ({
    top: el.offsetTop,
    bottom: el.offsetTop + el.offsetHeight,
  }));

/** Cuts the page at block boundaries so a card is never sliced in half. */
const computeCutPoints = (blocks, totalHeight, pageCapacity) => {
  const cuts = [];
  let cursor = 0;
  while (cursor < totalHeight) {
    let limit = cursor + pageCapacity;
    if (limit >= totalHeight) {
      cuts.push(totalHeight);
      break;
    }
    const fitting = blocks.filter((b) => b.top >= cursor && b.bottom <= limit);
    const overlapping = blocks.find((b) => b.top < limit && b.bottom > limit);
    if (overlapping && fitting.length) limit = fitting[fitting.length - 1].bottom;
    cuts.push(limit);
    cursor = limit;
  }
  return cuts;
};

/**
 * Builds and downloads the verification report PDF directly from data —
 * no dependency on any visible/mounted DOM node, so it can't go stale or
 * throw "report node is missing" errors.
 */
export const generateAnalysisReportPDF = async ({ analysisData, listId, meta = {}, fileName }) => {
  if (!Array.isArray(analysisData) || analysisData.length === 0) {
    throw new Error('No analysis data available to build a report.');
  }

  const html = buildReportHTML({ analysisData, listId, ...meta });
  const container = createOffscreenContainer(html);

  try {
    const images = Array.from(container.querySelectorAll('img'));
    await Promise.all(
      images.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((res) => {
              img.onload = res;
              img.onerror = res;
            })
      )
    );
    await wait(50); // let layout settle

    const blockBoundaries = getBlockBoundaries(container);
    const containerHeightPx = container.scrollHeight;

    const canvas = await html2canvas(container, {
      scale: CANVAS_SCALE,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: REPORT_WIDTH_PX,
    });

    const pdf = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const usableWidth = pageWidth - PAGE_MARGIN * 2;
    const usableHeight = pageHeight - PAGE_MARGIN * 2;

    const domToPt = usableWidth / REPORT_WIDTH_PX;
    const pageCapacityPx = usableHeight / domToPt;
    const cutPointsPx = computeCutPoints(blockBoundaries, containerHeightPx, pageCapacityPx);
    const canvasPxPerDomPx = canvas.width / REPORT_WIDTH_PX;

    const sliceCanvas = document.createElement('canvas');
    const ctx = sliceCanvas.getContext('2d');
    sliceCanvas.width = canvas.width;

    let cursorPx = 0;
    cutPointsPx.forEach((cutPx, i) => {
      const sliceHeightDomPx = cutPx - cursorPx;
      const sliceHeightCanvasPx = Math.round(sliceHeightDomPx * canvasPxPerDomPx);
      const sourceYCanvasPx = Math.round(cursorPx * canvasPxPerDomPx);

      sliceCanvas.height = sliceHeightCanvasPx;
      ctx.clearRect(0, 0, sliceCanvas.width, sliceHeightCanvasPx);
      ctx.drawImage(
        canvas,
        0,
        sourceYCanvasPx,
        canvas.width,
        sliceHeightCanvasPx,
        0,
        0,
        canvas.width,
        sliceHeightCanvasPx
      );

      const sliceDataUrl = sliceCanvas.toDataURL('image/jpeg', IMG_QUALITY);
      const sliceHeightPt = sliceHeightDomPx * domToPt;

      if (i > 0) pdf.addPage();
      pdf.addImage(
        sliceDataUrl,
        'JPEG',
        PAGE_MARGIN,
        PAGE_MARGIN,
        usableWidth,
        sliceHeightPt,
        undefined,
        'FAST'
      );
      cursorPx = cutPx;
    });

    // Footer + page numbers as native vector text — negligible file-size cost.
    const pageCount = pdf.internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p += 1) {
      pdf.setPage(p);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(140, 140, 140);
      pdf.text(
        'Generated by Urban Compass AI · AI-assisted document comparison',
        PAGE_MARGIN,
        pageHeight - 16
      );
      pdf.text(`Page ${p} of ${pageCount}`, pageWidth - PAGE_MARGIN, pageHeight - 16, {
        align: 'right',
      });
    }

    pdf.save(fileName || `verification-report-${listId || 'export'}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
};

export default generateAnalysisReportPDF;
